import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { exchangeCodeForTokens } from "./oidc.js";
import {
  getAuthBaseUrl,
  getOidcConfig,
  OIDC_PENDING_STORAGE_KEY,
} from "./config.js";
import { useAuthStore } from "./store.js";
import { useTenantContext } from "./TenantGate.js";
import type { Me, OidcPending } from "./types.js";
import { LoginCard } from "./LoginCard.js";

async function fetchMe(token: string, tenantSlug: string): Promise<Me> {
  // Auth path moved 2026-05-06: was /api/lyse/v1/auth/me; now lives at the
  // platform-level /api/auth/v1/me (.vibe/21_AUTH_NAMESPACE_RELOCATION.md).
  const res = await fetch(`${getAuthBaseUrl()}/me`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "X-Tenant-ID": tenantSlug,
    },
  });
  if (!res.ok) throw new Error(`me failed: ${String(res.status)}`);
  return (await res.json()) as Me;
}

function isSafeReturnTo(value: string | null | undefined): value is string {
  return (
    typeof value === "string" &&
    value.startsWith("/") &&
    !value.startsWith("//")
  );
}

export function CallbackPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tenantQuery = useTenantContext();
  const setSession = useAuthStore((s) => s.setSession);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = params.get("code");
    const state = params.get("state");
    if (!code || !state) {
      setError("Missing code or state in callback.");
      return;
    }
    if (!tenantQuery.tenant) {
      // Wait for tenant resolution before exchanging.
      return;
    }
    const tenant = tenantQuery.tenant;
    const pendingRaw = sessionStorage.getItem(OIDC_PENDING_STORAGE_KEY);
    if (!pendingRaw) {
      setError("Sign-in link expired. Please try again.");
      return;
    }
    const pending = JSON.parse(pendingRaw) as OidcPending;
    if (pending.state !== state) {
      setError("Sign-in link expired. Please try again.");
      return;
    }
    // PKCE one-time-use hardening. Drop the pending blob from sessionStorage
    // immediately after the state-match check, BEFORE the network call.
    // `pending.codeVerifier` and `pending.returnTo` are already captured in
    // memory below, so a duplicate tab landing on /auth/callback with the
    // same `code` cannot read the verifier and pass state validation a
    // second time. Zitadel would reject the duplicate `code` exchange
    // anyway, but defence-in-depth is cheap here.
    sessionStorage.removeItem(OIDC_PENDING_STORAGE_KEY);

    const cfg = getOidcConfig();
    let cancelled = false;
    const run = async (): Promise<void> => {
      try {
        const tokens = await exchangeCodeForTokens({
          issuer: cfg.issuer,
          code,
          codeVerifier: pending.codeVerifier,
          redirectUri: cfg.redirectUri,
          clientId: cfg.clientId,
        });
        const me = await fetchMe(tokens.access_token, tenant.tenantSlug);
        if (cancelled) return;
        // SB6-AUTH-RECONNECT-01 — stamp the three session-lifetime
        // fields on initial sign-in. `loggedInAt` anchors the 30-day
        // hard cap; `accessTokenExpiresAt` drives the proactive timer;
        // `lastActivityAt` seeds the idle clock so an immediate idle
        // refresh after a fresh sign-in is treated as active.
        const now = Date.now();
        setSession({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          user: me,
          tenant,
          accessTokenExpiresAt: now + tokens.expires_in * 1000,
          lastActivityAt: now,
          loggedInAt: now,
        });
        const dest = isSafeReturnTo(pending.returnTo)
          ? pending.returnTo
          : "/inspections";
        void navigate(dest, { replace: true });
      } catch (err) {
        if (cancelled) {
          // Component unmounted before exchange resolved — nothing to surface.
          return;
        }
        setError(err instanceof Error ? err.message : "Sign-in failed.");
      }
    };
    void run();

    return (): void => {
      cancelled = true;
    };
  }, [params, tenantQuery.tenant, navigate, setSession]);

  if (error) {
    return (
      <LoginCard>
        <Alert severity="error" role="alert">
          Sign-in failed. Please try again.
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate("/login", { replace: true })}
        >
          Back to sign in
        </Button>
      </LoginCard>
    );
  }

  return (
    <LoginCard>
      <Box
        aria-live="polite"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          py: 4,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          {/*
            aria-hidden so the visible "Signing you in…" Typography is the
            single accessible-name source. Otherwise axe flags the bare
            role="progressbar" with no name (rule: aria-progressbar-name).
            The aria-live="polite" wrapper above carries the live status to
            AT users.
          */}
          <CircularProgress size={32} aria-hidden="true" />
          <Typography variant="body1">Signing you in&hellip;</Typography>
        </Stack>
      </Box>
    </LoginCard>
  );
}
